/**
 * Client helpers for org document repository (bylaws / special rules).
 */

import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';

export const DOC_KINDS = [
  { value: 'bylaws', label: 'Bylaws' },
  { value: 'special_rules', label: 'Special rules of order' },
  { value: 'standing_rules', label: 'Standing rules' },
  { value: 'other', label: 'Other' },
];

export async function listOrgDocuments(organizationId) {
  if (!hasSupabaseConfig() || !organizationId) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('org_documents')
    .select(
      'id, organization_id, kind, title, storage_path, mime_type, byte_size, status, error_message, extracted_char_count, created_at, updated_at'
    )
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function countReadyOrgDocuments(organizationId) {
  if (!hasSupabaseConfig() || !organizationId) return 0;
  const supabase = getSupabaseClient();
  const { count, error } = await supabase
    .from('org_documents')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('status', 'ready');
  if (error) throw error;
  return count || 0;
}

function storagePath(organizationId, documentId, fileName) {
  const safe = String(fileName || 'document.txt')
    .replace(/[^\w.\-]+/g, '_')
    .slice(0, 120);
  return `org/${organizationId}/${documentId}/${safe}`;
}

/**
 * Upload a file, create org_documents row, then call ingest API.
 */
export async function uploadOrgDocument({
  organizationId,
  file,
  kind = 'bylaws',
  title = null,
  accessToken = null,
}) {
  if (!hasSupabaseConfig() || !organizationId || !file) {
    throw new Error('Missing organization or file');
  }
  const supabase = getSupabaseClient();
  const docId = crypto.randomUUID();
  const path = storagePath(organizationId, docId, file.name);
  const docTitle = title || file.name.replace(/\.[^.]+$/, '') || 'Document';

  const { error: upErr } = await supabase.storage.from('org-documents').upload(path, file, {
    upsert: false,
    contentType: file.type || 'application/octet-stream',
  });
  if (upErr) throw upErr;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data: row, error: insErr } = await supabase
    .from('org_documents')
    .insert({
      id: docId,
      organization_id: organizationId,
      kind,
      title: docTitle,
      storage_path: path,
      mime_type: file.type || null,
      byte_size: file.size,
      status: 'pending',
      uploaded_by: user?.id || null,
    })
    .select('*')
    .single();
  if (insErr) {
    await supabase.storage.from('org-documents').remove([path]).catch(() => {});
    throw insErr;
  }

  // Ingest must not fail the upload — row already exists (Pending until Ready).
  let ingestError = null;
  try {
    await triggerIngest(docId, accessToken, { retries: 2, retryDelayMs: 300 });
  } catch (err) {
    ingestError = err.message || 'Indexing did not finish';
  }

  return { ...row, ingestError };
}

/**
 * Create a document from pasted plain text (stored as .txt in Storage).
 */
export async function uploadOrgDocumentFromText({
  organizationId,
  text,
  kind = 'bylaws',
  title = 'Pasted bylaws',
  accessToken = null,
}) {
  const body = String(text || '').trim();
  if (body.length < 50) throw new Error('Paste at least a short section of text (50+ characters).');
  const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
  const file = new File([blob], `${title.replace(/[^\w\-]+/g, '_').slice(0, 40) || 'pasted'}.txt`, {
    type: 'text/plain',
  });
  return uploadOrgDocument({ organizationId, file, kind, title, accessToken });
}

export async function triggerIngest(documentId, accessToken = null, { retries = 0, retryDelayMs = 300 } = {}) {
  let token = accessToken;
  if (!token && hasSupabaseConfig()) {
    const supabase = getSupabaseClient();
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token || null;
  }

  let lastErr = null;
  const attempts = Math.max(1, retries + 1);
  for (let i = 0; i < attempts; i++) {
    const res = await fetch('/api/bylaws-ingest', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ documentId }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok) return json;

    lastErr = new Error(json.error || 'Ingest failed');
    lastErr.status = res.status;
    lastErr.body = json;

    const retryable = res.status === 404 || res.status >= 500;
    if (!retryable || i === attempts - 1) break;
    await new Promise((r) => setTimeout(r, retryDelayMs));
  }
  throw lastErr;
}

export async function deleteOrgDocument(document) {
  if (!document?.id || !hasSupabaseConfig()) return;
  const supabase = getSupabaseClient();
  if (document.storage_path) {
    await supabase.storage.from('org-documents').remove([document.storage_path]).catch(() => {});
  }
  const { error } = await supabase.from('org_documents').delete().eq('id', document.id);
  if (error) throw error;
}

export async function askMeetingAssistant({
  organizationId,
  question,
  sources = 'both',
  meetingContext = {},
  accessToken = null,
}) {
  let token = accessToken;
  if (!token && hasSupabaseConfig()) {
    const supabase = getSupabaseClient();
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token || null;
  }
  const res = await fetch('/api/meeting-assistant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ organizationId, question, sources, meetingContext }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || 'Assistant failed');
    err.status = res.status;
    err.code = json.code;
    err.body = json;
    throw err;
  }
  return json;
}
